import React, { useEffect, useState } from 'react';
import { Alert, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { ApiError, OrderService, ProjectSelectionLineInput, ProjectSelectionPageData } from '../api';
import MonkeybookWidgetFooter from '../MonkeybookWidgetFooter';
import RotatingLoader from '../ui/RotatingLoader';
import { useAccountData } from '../orderPage/components/AccountSpecificThemeWrapper';
import { OrderPageButton } from '../orderPage/themedComponents/OrderPageButton';
import { OrderPageTypography } from '../orderPage/themedComponents/OrderPageTypography';
import { priceFormatterFromCurrency } from '../orderPage/utils';
import { getDateFormat, getTimeFormat } from '../utils/getDateAndTimeFormat';
import { useLanguage } from '../utils/GeneralUtils';
import { ProjectSelectionPicker } from './ProjectSelectionPicker';
import { getRequiredProjectQuantity, getSelectedProjectQuantity } from './projectSelectionUtils';

enum ProjectSelectionPageError {
  NOT_FOUND = 'NOT_FOUND',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export const ProjectSelectionPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const account = useAccountData();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderid');

  const [page, setPage] = useState<ProjectSelectionPageData | undefined>(undefined);
  const [pageError, setPageError] = useState<ProjectSelectionPageError | undefined>(undefined);
  const [selection, setSelection] = useState<ProjectSelectionLineInput[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const loadPage = async () => {
    if (!orderId) {
      setPageError(ProjectSelectionPageError.NOT_FOUND);
      return;
    }
    try {
      const pageData = await OrderService.getProjectSelectionPage(orderId);
      setPage(pageData);
      setSelection(
        (pageData.selected_lines || []).map((line) => ({
          catalog_item_id: line.catalog_item_id!,
          quantity: line.quantity,
        })),
      );
    } catch (error) {
      setPageError(
        (error as ApiError)?.status === 404
          ? ProjectSelectionPageError.NOT_FOUND
          : ProjectSelectionPageError.NOT_AVAILABLE,
      );
    }
  };

  useEffect(() => {
    void loadPage();
  }, [orderId]);

  const handleSave = async () => {
    if (!orderId) {
      return;
    }
    setIsSaving(true);
    setSubmitError(undefined);
    try {
      await OrderService.submitProjectSelection({ order_id: orderId, lines: selection });
      setIsSaved(true);
      await loadPage();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.parent.postMessage({ name: 'monkeybook_scrollToTop' }, '*');
    } catch (error) {
      const detail = (error as ApiError)?.body?.detail;
      setSubmitError(typeof detail === 'string' ? detail : t('project_selection.save_failed'));
    }
    setIsSaving(false);
  };

  const renderSelectedSummary = (pageData: ProjectSelectionPageData) => {
    const selectedLines = pageData.selected_lines || [];
    if (!selectedLines.length) {
      return <OrderPageTypography>{t('project_selection.no_selection')}</OrderPageTypography>;
    }
    const catalogItems = (pageData.categories || []).flatMap((category) => category.items || []);
    return (
      <Grid container flexDirection="column" gap={1}>
        <OrderPageTypography variant="h6" bold>
          {t('project_selection.chosen_title')}
        </OrderPageTypography>
        {selectedLines.map((line) => {
          const catalogItem = catalogItems.find((item) => item.id === line.catalog_item_id);
          return (
            <Grid
              key={line.id}
              container
              alignItems="center"
              flexWrap="nowrap"
              gap={1.5}
              sx={{ border: `2px solid ${theme.customTheme.palette.border}`, borderRadius: '8px', padding: 1 }}>
              {catalogItem?.photo_url && (
                <img
                  src={catalogItem.photo_url}
                  alt={line.label}
                  style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }}
                />
              )}
              <Grid container flexDirection="column" flexGrow={1} minWidth={0}>
                <OrderPageTypography bold>{line.label}</OrderPageTypography>
                {line.quantity > 1 && <OrderPageTypography variant="body2">{`x${line.quantity}`}</OrderPageTypography>}
              </Grid>
              <OrderPageTypography bold style={{ whiteSpace: 'nowrap' }}>
                {priceFormatterFromCurrency(line.unit_price * line.quantity, account!.currency)}
              </OrderPageTypography>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderContent = () => {
    if (pageError) {
      return (
        <Alert severity="info">
          {pageError === ProjectSelectionPageError.NOT_FOUND
            ? t('project_selection.not_found')
            : t('project_selection.not_available')}
        </Alert>
      );
    }
    if (!page) {
      return (
        <Grid container justifyContent="center" p={4}>
          <RotatingLoader />
        </Grid>
      );
    }

    const requiredQuantity = getRequiredProjectQuantity(page.selection_count, page.persons);
    const selectedQuantity = getSelectedProjectQuantity(selection);
    const hasExistingSelection = (page.selected_lines || []).length > 0;
    const formattedDate = page.date ? moment(page.date).format(getDateFormat(account).longDateFormat) : undefined;
    const formattedTime =
      page.time != null ? moment().startOf('day').add(page.time, 'seconds').format(getTimeFormat(account)) : undefined;
    const orderPageLabel = page.order_page_label?.[language] || page.order_page_label?.en || page.order_page_label?.he;

    return (
      <Grid container flexDirection="column" gap={2}>
        <OrderPageTypography variant="h3" bold>
          {t('project_selection.title')}
        </OrderPageTypography>
        <OrderPageTypography variant="body2">
          {[
            page.customer_name,
            t('project_selection.order_summary', { orderNumber: page.order_number }),
            orderPageLabel,
            [formattedDate, formattedTime].filter(Boolean).join(' '),
          ]
            .filter(Boolean)
            .join(' · ')}
        </OrderPageTypography>

        {!page.selection_editable && (
          <>
            <Alert severity="info">
              {hasExistingSelection ? t('project_selection.locked') : t('project_selection.closed')}
            </Alert>
            {renderSelectedSummary(page)}
          </>
        )}

        {page.selection_editable && (
          <>
            {isSaved && <Alert severity="success">{t('project_selection.saved')}</Alert>}
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <ProjectSelectionPicker
              categories={page.categories || []}
              selectionCount={page.selection_count}
              requiredQuantity={requiredQuantity}
              currency={account!.currency}
              selection={selection}
              onChange={(newSelection) => {
                setIsSaved(false);
                setSelection(newSelection);
              }}
            />
            <Grid container justifyContent="center" mt={1}>
              <OrderPageButton
                variant="contained"
                loadingButton
                loading={isSaving}
                disabled={selectedQuantity !== requiredQuantity}
                $disabled={selectedQuantity !== requiredQuantity}
                onClick={() => {
                  void handleSave();
                }}>
                <OrderPageTypography color="button.contained">{t('project_selection.save')}</OrderPageTypography>
              </OrderPageButton>
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  return (
    <Grid>
      <Grid
        mb={2}
        container
        display="flex"
        justifyContent="space-around"
        sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        style={{ height: '100%', width: '100%' }}>
        <Grid
          container
          flexDirection="column"
          style={{
            borderRadius: '12px',
            background: theme.customTheme.palette.background,
            height: '100%',
            width: '100%',
            maxWidth: '710px',
          }}>
          <Grid
            px={isSmallScreen ? 2 : 4}
            container
            flexDirection="column"
            style={{
              color: theme.customTheme.palette.text.primary,
              border: `2px solid ${theme.customTheme.palette.border}`,
              borderRadius: '12px',
            }}
            boxShadow={4}
            py={2}
            mt={2}>
            {renderContent()}
          </Grid>
        </Grid>
      </Grid>
      <MonkeybookWidgetFooter
        languages={account?.customer_facing_widgets_languages}
        accountId={account?.id || page?.account_id || ''}
      />
    </Grid>
  );
};
